import { ChevronDown, RotateCcw } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useApp } from "@/lib/AppContext";
import { ApiError, type PipelineRun } from "@/lib/api";
import type { PipelineRunState } from "@/lib/usePipelineRun";
import { cn } from "@/lib/cn";

/**
 * SPCH-10 — "Restart at &lt;step&gt;", and the sentence that has to come before it.
 *
 * Restarting at or before `reconcile` rebuilds the transcript, and every flag,
 * correction and reviewed mark hangs off the segments that go with it. The Server
 * refuses such a request until it has been told the cost is accepted, and names what
 * would be lost; this catches that refusal and asks. Discovering it afterwards, on a
 * recording somebody spent an evening reading, is the outcome the whole exchange exists
 * to prevent.
 *
 * A split button rather than a picker beside a button: the ordinary case — running the
 * whole thing again — needs no choice at all, so it is the click itself. Restarting from
 * a later step is the exception, reached through the menu beside it.
 *
 * Its own file rather than living where it is drawn: the finished summary (`CompletePanel`
 * in `RecordingDetail`) is one caller, and the processing panel (`PipelinePanel`) — where a
 * failed or cancelled run also wants this, not only a finished one — is the other. Neither
 * should import the other's page module just to reach it.
 */
export function RestartControl({
  run,
  sourceId,
  state,
  onChanged,
}: {
  run: PipelineRun;
  sourceId: string;
  state: PipelineRunState;
  onChanged: () => void;
}) {
  const { api } = useApp();

  const firstStep = run.steps[0]?.stepName ?? "";

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<{ step: string; message: string } | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuRect, setMenuRect] = useState<{ top: number; right: number } | null>(null);

  const menuRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // A glass card elsewhere on the page (`backdrop-blur`, per M UI polish) opens its own
  // stacking context, and a later one in the DOM paints over this menu's z-index no
  // matter how high it is set — the two never compare directly. Rendering the dropdown
  // into a portal at the document root sidesteps every ancestor's stacking context
  // instead of trying to out-rank one of them.
  useLayoutEffect(() => {
    if (!menuOpen || !menuRef.current) return;

    function place() {
      const rect = menuRef.current!.getBoundingClientRect();
      setMenuRect({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }

    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;

    function onOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        !(dropdownRef.current && dropdownRef.current.contains(target))
      ) {
        setMenuOpen(false);
      }
    }
    function onEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setMenuOpen(false);
    }

    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEscape);
    };
  }, [menuOpen]);

  async function restart(step: string, discardReview: boolean) {
    if (busy || !step) return;

    setBusy(true);
    setProblem(null);

    try {
      state.adopt(await api.restartPipeline(sourceId, step, discardReview));
      setConfirming(null);
      onChanged();
    } catch (cause: unknown) {
      // 409 with a sentence naming what would be lost is the Server asking, not
      // failing. Anything else is a failure and reads as one.
      if (cause instanceof ApiError && cause.status === 409 && cause.message.includes("discard")) {
        setConfirming({ step, message: cause.message });
      } else {
        setProblem(cause instanceof ApiError ? cause.message : "The pipeline could not be restarted.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col items-end gap-[var(--space-2)]">
      <div ref={menuRef} className="relative flex items-stretch">
        <button
          type="button"
          disabled={busy || !firstStep}
          onClick={() => void restart(firstStep, false)}
          className="flex items-center gap-[var(--space-2)] rounded-l-[var(--radius-md)] border border-[var(--border-default)] px-[var(--space-3)] py-[4px] text-[length:var(--text-xs)] font-semibold text-[var(--text-primary)] disabled:opacity-50"
        >
          <RotateCcw size={12} strokeWidth={2} />
          {busy ? "Restarting…" : "Restart"}
        </button>

        <button
          type="button"
          disabled={busy || run.steps.length === 0}
          aria-label="Choose a step to restart at"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
          className="flex items-center justify-center rounded-r-[var(--radius-md)] border border-l-0 border-[var(--border-default)] px-[6px] py-[4px] text-[var(--text-secondary)] disabled:opacity-50"
        >
          <ChevronDown size={12} strokeWidth={2} />
        </button>

        {menuOpen &&
          menuRect &&
          createPortal(
            <div
              ref={dropdownRef}
              role="menu"
              aria-label="Restart at"
              style={{ top: menuRect.top, right: menuRect.right }}
              className="fixed z-[200] min-w-[160px] overflow-hidden rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-1)] py-1 shadow-[var(--shadow-lg)]"
            >
              {run.steps.map((candidate) => (
                <button
                  key={candidate.id}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    void restart(candidate.stepName, false);
                  }}
                  className={cn(
                    "block w-full px-[var(--space-3)] py-[6px] text-left font-[family-name:var(--font-mono)] text-[length:var(--text-xs)] text-[var(--text-primary)]",
                    "hover:bg-[var(--surface-2)]",
                  )}
                >
                  {candidate.stepName}
                </button>
              ))}
            </div>,
            document.body,
          )}
      </div>

      {confirming && (
        <div
          role="alert"
          className="max-w-[420px] rounded-[var(--radius-lg)] border border-[var(--warning)] bg-[var(--surface-glass)] px-[var(--space-3)] py-[var(--space-2)] text-right"
        >
          <p className="text-[length:var(--text-xs)] text-[var(--text-primary)]">{confirming.message}</p>

          <div className="mt-[var(--space-2)] flex justify-end gap-[var(--space-2)]">
            <button
              type="button"
              onClick={() => setConfirming(null)}
              className="rounded-[var(--radius-md)] px-[var(--space-2)] py-[2px] text-[length:var(--text-xs)] text-[var(--text-secondary)]"
            >
              Keep it
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void restart(confirming.step, true)}
              className="rounded-[var(--radius-md)] bg-[var(--warning)] px-[var(--space-3)] py-[2px] text-[length:var(--text-xs)] font-semibold text-[var(--text-inverse)] disabled:opacity-50"
            >
              Restart anyway
            </button>
          </div>
        </div>
      )}

      {problem && (
        <p role="alert" className="text-[length:var(--text-xs)] text-[var(--warning)]">
          {problem}
        </p>
      )}
    </div>
  );
}
